import { useFlag } from '@/services/featureFlags';
import { creditsToVideoMinutes } from '@/state/credits/credits';
import styled from '@emotion/styled';

const STREAMING_UNIT_COST = 0.5;
const STREAMING_UNIT_DURATION = 15;

/**
 * Calculates the cost of a campaign based on the duration, email amount, and click rate.
 *
 * @param {CampaignCostProps} params - The parameters for calculating the campaign cost.
 * @param {number} params.duration - The duration of the campaign in seconds.
 * @param {number} params.emailAmount - The number of emails sent for the campaign.
 * @param {number} params.clickRate - The click rate of the campaign. (0-100)
 * @returns {number} The calculated campaign cost.
 */
export const calculateCampaignCost = ({ duration, emailAmount, clickRate }: CampaignCostProps) => {
    const singleViewCost = Math.ceil(duration / STREAMING_UNIT_DURATION) * STREAMING_UNIT_COST;
    const amountOfViews = Math.ceil(emailAmount * (clickRate / 100));
    const cost = singleViewCost * amountOfViews;

    // Round up to the nearest 2 decimal places.
    return Math.ceil(cost * 100) / 100;
};

const Container = styled.div`
    margin-block-start: 12px;
    display: flex;
    width: 242px;
    padding: 12px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.primary.bgLight};
`;

const Title = styled.span`
    color: ${({ theme }) => theme.colors.black[600]};

    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 25px;
`;

const Result = styled.span`
    color: ${({ theme }) => theme.colors.primary.main};

    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
`;

interface CampaignCostProps {
    duration: number;
    emailAmount: number;
    clickRate: number;
}

export const CampaignCost = ({ duration, emailAmount, clickRate }: CampaignCostProps) => {
    const creditCost = calculateCampaignCost({ duration, emailAmount, clickRate });
    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);

    const costText = creditsToMinutesEnabled ? `${creditsToVideoMinutes(creditCost)} minutes` : `${creditCost} credits`;

    return (
        <Container>
            <Title>Est. campaign cost</Title>
            <Result>{costText}</Result>
        </Container>
    );
};
